
import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/17a4517b-926c-4e9c-a9d3-4ead1c9e99e4.png" alt="DEMO d. o. o." className="h-10 w-10 object-contain" />
            <span className={`text-xl font-medium tracking-wide ${scrolled ? 'text-gray-900' : 'text-white'}`}>DEMO d. o. o.</span>
          </div>
          <div className="flex items-center gap-12">
            <a href="#home" className={`text-sm font-medium transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>Home</a>
            <a href="#about" className={`text-sm font-medium transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>About</a>
            <a href="#products" className={`text-sm font-medium transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>Products</a>
            <a href="#contact" className={`text-sm font-medium transition-colors cursor-pointer ${scrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white/90 hover:text-white'}`}>Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1f3a] via-[#2d1b4e] to-[#1a1f3a]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        
        <div className="relative max-w-[1400px] w-full mx-auto px-8 py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="mb-8">
                <div className="text-7xl font-bold text-white mb-2">Modern iOS</div>
                <div className="text-6xl font-normal text-white/95 mb-2">Applications for</div>
                <div className="text-8xl font-bold text-white">Mobile Security</div>
              </h1>
            </div>
            
            <div className="flex flex-col items-end">
              <p className="text-white/70 text-lg leading-relaxed max-w-[420px] text-right">
                We are a team of experts developing modern applications for the iOS platform. Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="rounded-[32px] overflow-hidden aspect-[4/5] bg-gradient-to-br from-teal-100 via-purple-100 to-blue-100 relative">
                <img 
                  src="https://public.readdy.ai/ai/img_res/7831a302fbc8de9ac20cf3eea94999ef.jpg" 
                  alt="iOS Development" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-8 left-8 bg-white rounded-3xl px-5 py-2 shadow-lg">
                  <span className="text-sm font-medium text-gray-700">Since 2020</span>
                </div>
              </div>
            </div>
            
            <div>
              <div className="mb-6">
                <span className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Who We Are</span>
              </div>
              
              <h2 className="text-5xl font-bold text-gray-900 mb-8 leading-tight">Expert Team Building Secure iOS Solutions</h2>
              
              <div className="space-y-6 text-gray-600 text-base leading-relaxed max-w-[520px]">
                <p>
                  DEMO d. o. o. is a dedicated team of iOS development experts specializing in creating cutting-edge mobile applications that prioritize user security and experience. Our focus on the iOS platform allows us to deliver highly optimized and refined solutions.
                </p>
                <p>
                  With years of experience in mobile security and performance optimization, we understand the unique challenges faced by iOS users. Our products are built with meticulous attention to detail, ensuring they meet the highest standards of quality and reliability.
                </p>
                <p>
                  We believe in continuous innovation and staying ahead of emerging threats in the mobile landscape. Our commitment to excellence drives us to create applications that not only solve problems but also enhance the daily digital experience of our users.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="text-center mb-20">
            <span className="text-xs font-semibold tracking-widest text-gray-500 uppercase mb-4 block">Our Products</span>
            <h2 className="text-6xl font-bold text-gray-900 mb-2">Security Solutions</h2>
            <h3 className="text-6xl italic text-gray-700 font-serif">for iOS Platform</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* VPN Shield */}
            <div className="rounded-[40px] bg-[#FFE5E5] overflow-hidden aspect-[3/4] flex flex-col">
              <div className="flex-1 flex items-center justify-center p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iPhone%20mockup%20displaying%20VPN%20security%20shield%20app%20interface%20with%20blue%20gradient%20UI%20showing%20connected%20status%20and%20location%20selector%20on%20simple%20coral%20pink%20background%20centered%20composition%20minimalist%20product%20photography%20style&width=400&height=600&seq=demo-vpn-1&orientation=portrait" 
                  alt="VPN Shield App" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-white rounded-t-[40px] px-6 py-6 text-center">
                <h4 className="text-lg font-semibold text-gray-900 mb-1">VPN Shield</h4>
                <p className="text-sm text-gray-600">Secure browsing and privacy protection</p>
              </div>
            </div>

            {/* Device Cleaner */}
            <div className="rounded-[40px] bg-[#E5F9F5] overflow-hidden aspect-[3/4] flex flex-col">
              <div className="flex-1 flex items-center justify-center p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iPhone%20mockup%20showing%20device%20cleaner%20app%20interface%20with%20green%20gradient%20UI%20displaying%20storage%20optimization%20circular%20progress%20indicators%20and%20clean%20buttons%20on%20simple%20mint%20green%20background%20centered%20minimalist%20product%20shot&width=400&height=600&seq=demo-cleaner-1&orientation=portrait" 
                  alt="Device Cleaner App" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-white rounded-t-[40px] px-6 py-6 text-center">
                <h4 className="text-lg font-semibold text-gray-900 mb-1">Device Cleaner</h4>
                <p className="text-sm text-gray-600">Optimize storage and boost performance</p>
              </div>
            </div>

            {/* Privacy Guard */}
            <div className="rounded-[40px] bg-[#F0E5FF] overflow-hidden aspect-[3/4] flex flex-col">
              <div className="flex-1 flex items-center justify-center p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iPhone%20mockup%20displaying%20privacy%20guard%20security%20app%20interface%20with%20purple%20gradient%20UI%20showing%20app%20permissions%20lock%20icons%20and%20privacy%20settings%20on%20simple%20lavender%20background%20centered%20clean%20product%20photography&width=400&height=600&seq=demo-privacy-1&orientation=portrait" 
                  alt="Privacy Guard App" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-white rounded-t-[40px] px-6 py-6 text-center">
                <h4 className="text-lg font-semibold text-gray-900 mb-1">Privacy Guard</h4>
                <p className="text-sm text-gray-600">Control app permissions and data access</p>
              </div>
            </div>

            {/* Speed Optimizer */}
            <div className="rounded-[40px] bg-[#FFF5E5] overflow-hidden aspect-[3/4] flex flex-col">
              <div className="flex-1 flex items-center justify-center p-8">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20iPhone%20mockup%20showing%20speed%20optimizer%20app%20interface%20with%20orange%20gradient%20UI%20displaying%20performance%20metrics%20speedometer%20gauge%20and%20optimization%20controls%20on%20simple%20warm%20sand%20beige%20background%20centered%20minimalist%20style&width=400&height=600&seq=demo-speed-1&orientation=portrait" 
                  alt="Speed Optimizer App" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="bg-white rounded-t-[40px] px-6 py-6 text-center">
                <h4 className="text-lg font-semibold text-gray-900 mb-1">Speed Optimizer</h4>
                <p className="text-sm text-gray-600">Enhance device speed and responsiveness</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <section id="contact" className="bg-[#1A202C] py-20">
        <div className="max-w-[1400px] mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            {/* Brand Column */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/17a4517b-926c-4e9c-a9d3-4ead1c9e99e4.png" alt="DEMO d. o. o." className="h-8 w-8 object-contain" />
                <span className="text-xl font-medium text-white">DEMO d. o. o.</span>
              </div>
              <p className="text-[#A0AEC0] text-sm leading-relaxed max-w-[280px]">
                Expert team developing modern iOS applications focused on mobile security and user experience enhancement.
              </p>
            </div>

            {/* Location Column */}
            <div>
              <h4 className="text-xs font-semibold tracking-widest text-[#A0AEC0] uppercase mb-6">Location</h4>
              <div className="space-y-2 text-white text-[15px]">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-[#14B8A6] w-5 h-5 flex items-center justify-center"></i>
                  <span>Supilova 48</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-building-line text-[#14B8A6] w-5 h-5 flex items-center justify-center"></i>
                  <span>City Delnice</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-mail-line text-[#14B8A6] w-5 h-5 flex items-center justify-center"></i>
                  <span>Zip 51300</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-global-line text-[#14B8A6] w-5 h-5 flex items-center justify-center"></i>
                  <span>Country Croatia</span>
                </div>
              </div>
            </div>

            {/* Contact Column */}
            <div>
              <h4 className="text-xs font-semibold tracking-widest text-[#A0AEC0] uppercase mb-6">Get In Touch</h4>
              <div className="space-y-4 text-white text-[15px]">
                <div className="flex items-start gap-3">
                  <i className="ri-mail-send-line text-[#14B8A6] w-5 h-5 flex items-center justify-center"></i>
                  <span>help@demodoo.com</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-phone-line text-[#14B8A6] w-5 h-5 flex items-center justify-center"></i>
                  <span>+1 6812558586</span>
                </div>
              </div>
            </div>

            {/* Company Column */}
            <div>
              <h4 className="text-xs font-semibold tracking-widest text-[#A0AEC0] uppercase mb-6">Company</h4>
              <div className="space-y-3 text-[#A0AEC0] text-[15px]">
                <div>iOS Development</div>
                <div>Mobile Security</div>
                <div>App Solutions</div>
              </div>
            </div>
          </div>

          {/* Large Brand Text */}
          <div className="relative py-12 border-t border-gray-700">
            <div className="text-[120px] font-bold text-[#2D3748] tracking-tighter leading-none select-none">
              DEMO
            </div>
            <div className="mt-6 text-sm text-[#A0AEC0] flex items-center justify-between">
              <span>© 2025 DEMO d. o. o. All rights reserved.</span>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-[#14B8A6] hover:text-[#0F9B8E] transition-colors cursor-pointer whitespace-nowrap">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
